/**
 * VH-83: Manual recipe entry form.
 *
 * Drives the same payload shape as POST /recipes (RecipeCreateRequest). Used
 * standalone (Manual tab) and as the editor for an AI/photo draft.
 *
 * The form intentionally uses a controlled local state object instead of
 * react-hook-form's complex field arrays — ingredients/steps are nested
 * arrays of objects with their own children, and direct state is simpler for
 * the relatively small number of rows we expect.
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import IngredientPicker from './IngredientPicker'
import { roundAmount } from '../../utils/roundAmount'
import RecipeStepEditor from './RecipeStepEditor'
import EquipmentTagsInput from './EquipmentTagsInput'

const emptyIngredient = (order) => ({
  food_name_snapshot: '',
  amount: '',
  unit: 'g',
  display_order: order,
  notes: '',
})

const emptyStep = (order) => ({
  step_order: order,
  text: '',
  timer_minutes: null,
  min_oven_temp_c: null,
})

const sanitizeIngredient = (ing) => ({
  ...ing,
  amount: roundAmount(ing.amount) || 0,
})

export default function CaptureManualForm({
  initialValue,
  categories = [],
  onSubmit,
  submitting = false,
  submitLabelKey = 'recipes.capture.saveDraft',
}) {
  const { t } = useTranslation()
  const [errors, setErrors] = useState({})

  const defaults = useMemo(
    () => ({
      title: initialValue?.title || '',
      description: initialValue?.description || '',
      tips: initialValue?.tips || '',
      photo_url: initialValue?.photo_url || '',
      servings: initialValue?.servings || 1,
      prep_minutes: initialValue?.prep_minutes ?? null,
      cook_minutes: initialValue?.cook_minutes ?? null,
      change_reason: initialValue?.change_reason || '',
      branch_label: initialValue?.branch_label || '',
      ingredients:
        initialValue?.ingredients?.length > 0
          ? initialValue.ingredients.map((i, idx) => ({
              ...i,
              // VH-263: a scaled draft arrives with float noise ("0.11399999999999999");
              // show the number a cook would write.
              amount: roundAmount(i.amount),
              display_order: i.display_order ?? idx,
            }))
          : [emptyIngredient(0)],
      steps:
        initialValue?.steps?.length > 0
          ? initialValue.steps.map((s, idx) => ({
              ...s,
              step_order: s.step_order ?? idx + 1,
            }))
          : [],
      equipment_keys: initialValue?.equipment_keys || [],
      category_id: initialValue?.category_id || '',
    }),
    [initialValue]
  )

  const [form, setForm] = useState(defaults)

  // Re-seed when the initialValue identity changes (e.g. AI draft adopted).
  useEffect(() => {
    setForm(defaults)
    setErrors({})
  }, [defaults])

  const update = (patch) => setForm((f) => ({ ...f, ...patch }))

  const updateIngredient = (idx, next) => {
    setForm((f) => {
      const arr = [...f.ingredients]
      arr[idx] = next
      return { ...f, ingredients: arr }
    })
  }

  const removeIngredient = (idx) => {
    setForm((f) => {
      const arr = f.ingredients.filter((_, i) => i !== idx)
      return {
        ...f,
        ingredients:
          arr.length > 0 ? arr : [emptyIngredient(0)],
      }
    })
  }

  const addIngredient = () => {
    setForm((f) => ({
      ...f,
      ingredients: [...f.ingredients, emptyIngredient(f.ingredients.length)],
    }))
  }

  const updateStep = (idx, next) => {
    setForm((f) => {
      const arr = [...f.steps]
      arr[idx] = next
      return { ...f, steps: arr }
    })
  }

  const removeStep = (idx) => {
    setForm((f) => {
      const arr = f.steps
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, step_order: i + 1 }))
      return { ...f, steps: arr }
    })
  }

  const addStep = () => {
    setForm((f) => ({
      ...f,
      steps: [...f.steps, emptyStep(f.steps.length + 1)],
    }))
  }

  const validate = () => {
    const e = {}
    if (!form.title.trim()) e.title = t('recipes.manual.titleRequired')
    if (!form.category_id) e.category_id = t('recipes.manual.categoryRequired')
    const hasIngredients = form.ingredients.some(
      (i) => i.food_name_snapshot && i.food_name_snapshot.trim()
    )
    if (!hasIngredients)
      e.ingredients = t('recipes.manual.ingredientsRequired')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = (ev) => {
    ev.preventDefault()
    if (!validate()) return
    const cleanIngredients = form.ingredients
      .filter((i) => i.food_name_snapshot && i.food_name_snapshot.trim())
      .map(sanitizeIngredient)
    const payload = {
      category_id: parseInt(form.category_id, 10),
      version: {
        title: form.title.trim(),
        description: form.description.trim() || null,
        tips: form.tips.trim() || null,
        photo_url: form.photo_url.trim() || null,
        servings: parseInt(form.servings, 10) || 1,
        prep_minutes: form.prep_minutes,
        cook_minutes: form.cook_minutes,
        change_reason: form.change_reason || null,
        branch_label: form.branch_label || null,
        ingredients: cleanIngredients,
        steps: form.steps
          .filter((s) => s.text && s.text.trim())
          .map((s, i) => ({ ...s, step_order: i + 1 })),
        equipment_keys: form.equipment_keys,
      },
    }
    onSubmit?.(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="form-label">
            {t('recipes.manual.titleField')}
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder={t('recipes.manual.titlePlaceholder')}
            className="form-input w-full"
          />
          {errors.title && <p className="form-error">{errors.title}</p>}
        </div>

        <div>
          <label className="form-label">
            {t('recipes.manual.categoryField')}
          </label>
          <select
            value={form.category_id || ''}
            onChange={(e) => update({ category_id: e.target.value })}
            className="form-input w-full"
          >
            <option value="">{t('recipes.manual.selectCategory')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
          {errors.category_id && (
            <p className="form-error">{errors.category_id}</p>
          )}
        </div>

        <div>
          <label className="form-label">
            {t('recipes.manual.servingsField')}
          </label>
          <input
            type="number"
            min="1"
            value={form.servings}
            onChange={(e) =>
              update({ servings: parseInt(e.target.value, 10) || 1 })
            }
            className="form-input w-full"
          />
        </div>

        <div>
          <label className="form-label">
            {t('recipes.manual.prepMinutesField')}
          </label>
          <input
            type="number"
            min="0"
            value={form.prep_minutes ?? ''}
            onChange={(e) =>
              update({
                prep_minutes:
                  e.target.value === ''
                    ? null
                    : parseInt(e.target.value, 10),
              })
            }
            className="form-input w-full"
          />
        </div>

        <div>
          <label className="form-label">
            {t('recipes.manual.cookMinutesField')}
          </label>
          <input
            type="number"
            min="0"
            value={form.cook_minutes ?? ''}
            onChange={(e) =>
              update({
                cook_minutes:
                  e.target.value === ''
                    ? null
                    : parseInt(e.target.value, 10),
              })
            }
            className="form-input w-full"
          />
        </div>

        <div className="md:col-span-2">
          <label className="form-label">
            {t('recipes.manual.descriptionField')}
          </label>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder={t('recipes.manual.descriptionPlaceholder')}
            className="form-input w-full"
          />
        </div>

        <div className="md:col-span-2">
          <label className="form-label">
            {t('recipes.manual.photoField')}
          </label>
          <input
            type="url"
            value={form.photo_url}
            onChange={(e) => update({ photo_url: e.target.value })}
            placeholder={t('recipes.manual.photoUrlPlaceholder')}
            className="form-input w-full"
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('recipes.manual.photoHelp')}
          </p>
          {errors.photo_url && (
            <p className="form-error">{errors.photo_url}</p>
          )}
        </div>
      </div>

      {/* Ingredients */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">
            {t('recipes.manual.ingredientsField')}
          </h3>
          <button
            type="button"
            onClick={addIngredient}
            className="btn-secondary text-sm flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" />
            {t('recipes.manual.addIngredient')}
          </button>
        </div>
        <div className="space-y-2">
          {form.ingredients.map((ing, idx) => (
            <IngredientPicker
              key={idx}
              index={idx}
              value={ing}
              onChange={(v) => updateIngredient(idx, v)}
              onRemove={() => removeIngredient(idx)}
            />
          ))}
        </div>
        {errors.ingredients && (
          <p className="form-error">{errors.ingredients}</p>
        )}
      </section>

      {/* Steps */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">
            {t('recipes.manual.stepsField')}
          </h3>
          <button
            type="button"
            onClick={addStep}
            className="btn-secondary text-sm flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" />
            {t('recipes.manual.addStep')}
          </button>
        </div>
        <div className="space-y-2">
          {form.steps.map((step, idx) => (
            <RecipeStepEditor
              key={idx}
              index={idx}
              value={step}
              onChange={(v) => updateStep(idx, v)}
              onRemove={() => removeStep(idx)}
            />
          ))}
        </div>
      </section>

      {/* Equipment */}
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          {t('recipes.manual.equipmentField')}
        </h3>
        <EquipmentTagsInput
          value={form.equipment_keys}
          onChange={(keys) => update({ equipment_keys: keys })}
        />
      </section>

      {/* VH-106: Tips & Recommendations */}
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          {t('recipes.manual.tipsField')}
        </h3>
        <textarea
          rows={4}
          maxLength={4000}
          value={form.tips}
          onChange={(e) => update({ tips: e.target.value })}
          placeholder={t('recipes.manual.tipsPlaceholder')}
          className="form-input w-full"
          data-testid="recipe-tips-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('recipes.manual.tipsHelp')}
        </p>
      </section>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary flex items-center gap-2"
          data-testid="submit-recipe"
        >
          {submitting ? (
            <>
              <XMarkIcon className="h-4 w-4 animate-pulse" />
              {t('recipes.capture.saving')}
            </>
          ) : (
            t(submitLabelKey)
          )}
        </button>
      </div>
    </form>
  )
}
